"use client";

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Columns,
  X,
  Trash2,
  Download,
  ChevronDown,
  Eye,
  ExternalLink,
  Save,
  Star,
  StarOff,
  FileSpreadsheet,
  ChevronUp,
  DollarSign,
  AlertTriangle,
  Clock,
  BarChart3,
  Settings,
  Receipt,
  Mail,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MultiSelect } from "@/components/ui/multiselect";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { formatDate, formatDateDDMMYYYY, formatCurrency, cn } from "@/lib/utils";
import { useTransactionsPageData } from "@/hooks/useTransactionsData";
import {
  Transaction,
  TransactionFilters,
  SavedFilterPreset,
  ColumnVisibility,
  TransactionType,
  TransactionStatus,
} from "@/types/transaction";
import { TransactionTypeBadge } from "@/components/transactions/transaction-type-badge";
import { TransactionStatusBadge } from "@/components/transactions/transaction-status-badge";
import { FilterSheet } from "@/components/transactions/filter-sheet";
import { TransactionDetailSheet } from "@/components/transactions/transaction-detail-sheet";
import { SelectionToolbar } from "@/components/transactions/selection-toolbar";
import { SendRemindersDialog } from "@/components/transactions/send-reminders-dialog";
import { AllocateReceiptDialog } from "@/components/transactions/allocate-receipt-dialog";
import { ColumnConfigDialog } from "@/components/transactions/column-config-dialog";
import { exportTransactionsToExcel, calculateTransactionTotals } from "@/lib/export-utils";

// Mock customers for filter
const mockCustomers = [
  { value: "CUST001", label: "Acme Corporation" },
  { value: "CUST002", label: "Global Industries Ltd" },
  { value: "CUST003", label: "Tech Solutions Inc" },
  { value: "CUST004", label: "Premier Services Group" },
  { value: "CUST005", label: "Enterprise Systems Co" },
];

// Default presets
const defaultPresets: SavedFilterPreset[] = [
  {
    id: "preset-1",
    name: "Overdue Invoices",
    filters: {
      customerIds: [],
      transactionTypes: ["invoice"],
      statuses: ["overdue"],
      searchQuery: "",
    },
    isDefault: true,
  },
  {
    id: "preset-2",
    name: "Recent Receipts",
    filters: {
      customerIds: [],
      transactionTypes: ["receipt"],
      statuses: ["completed"],
      searchQuery: "",
    },
    isDefault: true,
  },
];

type SortColumn = keyof Transaction | null;
type SortDirection = "asc" | "desc";
type MetricFilter = "outstanding" | "overdue" | "at-risk" | "unallocated-receipts" | null;

export function TransactionsPage() {
  const navigate = useNavigate();

  // Data from hook (handles Demo/Live mode)
  const { transactions, isLoading, error } = useTransactionsPageData();

  // State
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMetricFilter, setSelectedMetricFilter] = useState<MetricFilter>(null);
  const [filters, setFilters] = useState<TransactionFilters>({
    customerIds: [],
    transactionTypes: [],
    statuses: [],
    searchQuery: "",
    outstandingBalanceMin: 0.01, // Default filter: Outstanding Balance > 0
  });
  const [savedPresets, setSavedPresets] = useState<SavedFilterPreset[]>(defaultPresets);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>({
    referenceNumber: true,
    transactionDate: true,
    customerName: true,
    transactionType: true,
    amount: true,
    outstandingAmount: true, // Now visible by default
    status: true,
    description: true,
    createdBy: true,
    modifiedBy: false,
    dueDate: true,
    actions: true,
    // New optional columns (hidden by default)
    accountCode: false,
    netAmount: false,
    vatAmount: false,
    customerReference: false,
    periodNumber: false,
    year: false,
    onDispute: false,
    subLedger: false,
    orderNumber: false,
  });
  const [sortColumn, setSortColumn] = useState<SortColumn>("transactionDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Sheet/Dialog states
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [sendRemindersDialogOpen, setSendRemindersDialogOpen] = useState(false);
  const [allocateReceiptDialogOpen, setAllocateReceiptDialogOpen] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  // Apply filters
  const filteredTransactions = useMemo(() => {
    let result = [...transactions];
    const today = new Date();

    // Metric filter (applied first, before other filters)
    if (selectedMetricFilter) {
      if (selectedMetricFilter === "outstanding") {
        // Total Outstanding - all outstanding/pending invoices
        result = result.filter(
          (t) =>
            t.transactionType === "invoice" &&
            (t.status === "pending" || t.status === "overdue" || t.status === "partially_paid")
        );
      } else if (selectedMetricFilter === "overdue") {
        // Overdue - transactions past their due date
        result = result.filter((t) => {
          if (!t.dueDate) return false;
          const dueDate = new Date(t.dueDate);
          return t.status === "overdue" || (dueDate < today && t.status !== "completed");
        });
      } else if (selectedMetricFilter === "at-risk") {
        // At Risk - invoices overdue or due within 7 days
        result = result.filter((t) => {
          if (t.transactionType !== "invoice" || !t.dueDate) return false;
          const dueDate = new Date(t.dueDate);
          const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          return t.status === "overdue" || (daysUntilDue <= 7 && daysUntilDue >= 0 && t.status !== "completed");
        });
      } else if (selectedMetricFilter === "unallocated-receipts") {
        // Unallocated Receipts - receipts with outstanding balance > 0
        result = result.filter((t) =>
          t.transactionType === "receipt" &&
          t.outstandingAmount !== undefined &&
          t.outstandingAmount > 0
        );
      }
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.referenceNumber.toLowerCase().includes(query) ||
          t.customerName.toLowerCase().includes(query) ||
          t.description.toLowerCase().includes(query)
      );
    }

    // Customer filter
    if (filters.customerIds.length > 0) {
      result = result.filter((t) => filters.customerIds.includes(t.customerId));
    }

    // Transaction type filter
    if (filters.transactionTypes.length > 0) {
      result = result.filter((t) => filters.transactionTypes.includes(t.transactionType));
    }

    // Status filter
    if (filters.statuses.length > 0) {
      result = result.filter((t) => filters.statuses.includes(t.status));
    }

    // Date range filter
    if (filters.dateFrom) {
      result = result.filter((t) => t.transactionDate >= filters.dateFrom!);
    }
    if (filters.dateTo) {
      result = result.filter((t) => t.transactionDate <= filters.dateTo!);
    }

    // Amount range filter
    if (filters.amountMin !== undefined) {
      result = result.filter((t) => t.amount >= filters.amountMin!);
    }
    if (filters.amountMax !== undefined) {
      result = result.filter((t) => t.amount <= filters.amountMax!);
    }

    // Outstanding Balance filter (DEFAULT FILTER)
    if (filters.outstandingBalanceMin !== undefined) {
      result = result.filter((t) => {
        // If outstandingAmount is not defined, treat as 0
        const outstandingBalance = t.outstandingAmount ?? 0;
        return outstandingBalance >= filters.outstandingBalanceMin!;
      });
    }
    if (filters.outstandingBalanceMax !== undefined) {
      result = result.filter((t) => {
        const outstandingBalance = t.outstandingAmount ?? 0;
        return outstandingBalance <= filters.outstandingBalanceMax!;
      });
    }

    return result;
  }, [transactions, searchQuery, filters, selectedMetricFilter]);

  // Sort transactions
  const sortedTransactions = useMemo(() => {
    if (!sortColumn) return filteredTransactions;

    return [...filteredTransactions].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

      let comparison = 0;
      if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredTransactions, sortColumn, sortDirection]);

  // Paginate
  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedTransactions.slice(startIndex, startIndex + pageSize);
  }, [sortedTransactions, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedTransactions.length / pageSize);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const today = new Date();

    // Total Outstanding - sum of all outstanding/pending invoices
    const totalOutstanding = filteredTransactions
      .filter((t) =>
        t.transactionType === "invoice" &&
        (t.status === "pending" || t.status === "overdue" || t.status === "partially_paid")
      )
      .reduce((sum, t) => sum + t.amount, 0);

    // Overdue - sum of transactions past their due date
    const overdueAmount = filteredTransactions
      .filter((t) => {
        if (!t.dueDate) return false;
        const dueDate = new Date(t.dueDate);
        return t.status === "overdue" || (dueDate < today && t.status !== "completed");
      })
      .reduce((sum, t) => sum + t.amount, 0);

    // At Risk Amount - invoices that are overdue or approaching due date (within 7 days)
    const atRiskAmount = filteredTransactions
      .filter((t) => {
        if (t.transactionType !== "invoice" || !t.dueDate) return false;
        const dueDate = new Date(t.dueDate);
        const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        // At risk if overdue or due within 7 days
        return t.status === "overdue" || (daysUntilDue <= 7 && daysUntilDue >= 0 && t.status !== "completed");
      })
      .reduce((sum, t) => sum + t.amount, 0);

    // Unallocated Receipts - receipts with outstanding balance > 0
    const unallocatedReceipts = filteredTransactions
      .filter((t) =>
        t.transactionType === "receipt" &&
        t.outstandingAmount !== undefined &&
        t.outstandingAmount > 0
      );

    const unallocatedReceiptsCount = unallocatedReceipts.length;
    const unallocatedReceiptsAmount = unallocatedReceipts.reduce((sum, t) => sum + (t.outstandingAmount || 0), 0);

    // Ageing Breakdown - categorize outstanding invoices by age
    const ageing = {
      current: 0,      // Not yet due
      days1_30: 0,     // 1-30 days overdue
      days31_60: 0,    // 31-60 days overdue
      days61_90: 0,    // 61-90 days overdue
      days90Plus: 0,   // 90+ days overdue
    };

    filteredTransactions
      .filter((t) =>
        t.transactionType === "invoice" &&
        (t.status === "pending" || t.status === "overdue" || t.status === "partially_paid")
      )
      .forEach((t) => {
        if (!t.dueDate) {
          ageing.current += t.amount;
          return;
        }

        const dueDate = new Date(t.dueDate);
        const daysOverdue = Math.ceil((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));

        if (daysOverdue < 0) {
          ageing.current += t.amount;
        } else if (daysOverdue <= 30) {
          ageing.days1_30 += t.amount;
        } else if (daysOverdue <= 60) {
          ageing.days31_60 += t.amount;
        } else if (daysOverdue <= 90) {
          ageing.days61_90 += t.amount;
        } else {
          ageing.days90Plus += t.amount;
        }
      });

    return {
      totalOutstanding,
      overdueAmount,
      atRiskAmount,
      unallocatedReceiptsCount,
      unallocatedReceiptsAmount,
      ageing,
    };
  }, [filteredTransactions]);

  // Handlers
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(paginatedTransactions.map((t) => t.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };

  const handleMetricCardClick = (metric: MetricFilter) => {
    // Toggle: if clicking the same metric, deselect it
    if (selectedMetricFilter === metric) {
      setSelectedMetricFilter(null);
      toast.success("Filter cleared");
    } else {
      setSelectedMetricFilter(metric);
      setCurrentPage(1);
      const metricNames = {
        outstanding: "Total Outstanding",
        overdue: "Overdue",
        "at-risk": "At Risk Amount",
        "unallocated-receipts": "Unallocated Receipts",
      };
      toast.success(`Filtering by ${metricNames[metric]}`);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFilters({
      customerIds: [],
      transactionTypes: [],
      statuses: [],
      searchQuery: "",
    });
    setSelectedMetricFilter(null);
    setActivePresetId(null);
    setCurrentPage(1);
    toast.success("Filters cleared");
  };

  const handleApplyPreset = (preset: SavedFilterPreset) => {
    setFilters(preset.filters);
    setSearchQuery(preset.filters.searchQuery || "");
    setActivePresetId(preset.id);
    setCurrentPage(1);
    setFilterSheetOpen(false);
    toast.success(`Filter preset "${preset.name}" applied`);
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) {
      toast.error("Preset name required");
      return;
    }

    const newPreset: SavedFilterPreset = {
      id: `preset-${Date.now()}`,
      name: presetName,
      filters: { ...filters, searchQuery },
    };

    setSavedPresets([...savedPresets, newPreset]);
    setPresetName("");
    setSavePresetDialogOpen(false);
    toast.success(`Filter preset "${newPreset.name}" saved`);
  };

  const handleDeletePreset = (presetId: string) => {
    const preset = savedPresets.find((p) => p.id === presetId);
    if (preset?.isDefault) return;

    setSavedPresets(savedPresets.filter((p) => p.id !== presetId));
    if (activePresetId === presetId) {
      setActivePresetId(null);
    }
    toast.success("Filter preset deleted");
  };

  const handleViewTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDetailSheetOpen(true);
  };

  const handleDeleteTransaction = (transaction: Transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (transactionToDelete) {
      // TODO: Implement proper delete via API and refetch
      // setTransactions(transactions.filter((t) => t.id !== transactionToDelete.id));
      toast.success("Transaction deleted");
      setTransactionToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleBulkDelete = () => {
    // TODO: Implement proper bulk delete via API and refetch
    // setTransactions(transactions.filter((t) => !selectedRows.has(t.id)));
    toast.success(`${selectedRows.size} transactions deleted`);
    setSelectedRows(new Set());
  };

  const handleExport = (format: "csv" | "excel" | "pdf") => {
    const exportData = selectedRows.size > 0
      ? transactions.filter((t) => selectedRows.has(t.id))
      : sortedTransactions;

    try {
      exportTransactionsToExcel(exportData);
      toast.success(`Exported ${exportData.length} transactions successfully`);
    } catch (error) {
      toast.error("Failed to export transactions");
    }
  };

  const handleExportSelected = () => {
    const exportData = transactions.filter((t) => selectedRows.has(t.id));
    try {
      exportTransactionsToExcel(exportData);
      toast.success(`Exported ${exportData.length} selected transactions`);
    } catch (error) {
      toast.error("Failed to export selected transactions");
    }
  };

  const handleSendReminders = (transactionIds: number[], message: string) => {
    // In a real app, this would call an API
    toast.success(`Payment reminders sent for ${transactionIds.length} invoice(s)`);
  };

  const handleAllocateReceipt = (receiptId: number, allocations: any[]) => {
    // In a real app, this would call an API
    toast.success(`Receipt allocated to ${allocations.length} invoice(s)`);
  };

  // Column configuration handler - receives config and order from ColumnConfigDialog
  const handleColumnConfigApply = (config: ColumnVisibility, order: string[]) => {
    setColumnVisibility(config);
    setColumnOrder(order);
  };

  // Get selected transactions
  const selectedTransactions = useMemo(() => {
    return transactions.filter((t) => selectedRows.has(t.id));
  }, [transactions, selectedRows]);

  // Calculate totals for selected transactions
  const selectionTotals = useMemo(() => {
    return calculateTransactionTotals(selectedTransactions);
  }, [selectedTransactions]);

  const handleViewDocument = (transaction: Transaction) => {
    if (transaction.documentUrl) {
      window.open(transaction.documentUrl, "_blank");
      toast.success("Document opened");
    } else {
      toast.error("No document available");
    }
  };

  // Check if there are any non-default filters active
  const hasActiveFilters =
    searchQuery ||
    filters.customerIds.length > 0 ||
    filters.transactionTypes.length > 0 ||
    filters.statuses.length > 0 ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.amountMin !== undefined ||
    filters.amountMax !== undefined ||
    // Outstanding balance filter is non-default if it's not 0.01 or if max is set
    (filters.outstandingBalanceMin !== undefined && filters.outstandingBalanceMin !== 0.01) ||
    filters.outstandingBalanceMax !== undefined;

  // Count active filters (excluding the default outstanding balance filter)
  const activeFilterCount =
    (searchQuery ? 1 : 0) +
    (filters.customerIds.length > 0 ? 1 : 0) +
    (filters.transactionTypes.length > 0 ? 1 : 0) +
    (filters.statuses.length > 0 ? 1 : 0) +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0) +
    (filters.amountMin !== undefined ? 1 : 0) +
    (filters.amountMax !== undefined ? 1 : 0) +
    // Count outstanding balance filter only if it's been modified from default
    ((filters.outstandingBalanceMin !== undefined && filters.outstandingBalanceMin !== 0.01) ||
      filters.outstandingBalanceMax !== undefined ? 1 : 0);

  // Check if default filter is active
  const hasDefaultFilter = filters.outstandingBalanceMin === 0.01;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Transactions</h1>
        <p className="text-muted-foreground">Comprehensive view of all transaction records</p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card
          className={cn(
            "transition-all border-l-4 border-l-orange-500 cursor-pointer",
            selectedMetricFilter === "outstanding"
              ? "shadow-lg ring-2 ring-orange-500 bg-orange-50/50"
              : "hover:shadow-md"
          )}
          onClick={() => handleMetricCardClick("outstanding")}
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-orange-100">
                    <DollarSign className="h-4 w-4 text-orange-600" />
                  </div>
                  {selectedMetricFilter === "outstanding" && (
                    <Badge variant="secondary" className="text-xs">Active</Badge>
                  )}
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Total Outstanding
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(summaryMetrics.totalOutstanding, "USD")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  All outstanding transactions
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            "transition-all border-l-4 border-l-red-500 cursor-pointer",
            selectedMetricFilter === "overdue"
              ? "shadow-lg ring-2 ring-red-500 bg-red-50/50"
              : "hover:shadow-md"
          )}
          onClick={() => handleMetricCardClick("overdue")}
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-red-100">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  </div>
                  {selectedMetricFilter === "overdue" && (
                    <Badge variant="secondary" className="text-xs">Active</Badge>
                  )}
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Overdue
                </p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(summaryMetrics.overdueAmount, "USD")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Past due date
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            "transition-all border-l-4 border-l-yellow-500 cursor-pointer",
            selectedMetricFilter === "at-risk"
              ? "shadow-lg ring-2 ring-yellow-500 bg-yellow-50/50"
              : "hover:shadow-md"
          )}
          onClick={() => handleMetricCardClick("at-risk")}
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-yellow-100">
                    <Clock className="h-4 w-4 text-yellow-600" />
                  </div>
                  {selectedMetricFilter === "at-risk" && (
                    <Badge variant="secondary" className="text-xs">Active</Badge>
                  )}
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  At Risk Amount
                </p>
                <p className="text-2xl font-bold text-yellow-600">
                  {formatCurrency(summaryMetrics.atRiskAmount, "USD")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Due within 7 days or overdue
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            "transition-all border-l-4 border-l-purple-500 cursor-pointer",
            selectedMetricFilter === "unallocated-receipts"
              ? "shadow-lg ring-2 ring-purple-500 bg-purple-50/50"
              : "hover:shadow-md"
          )}
          onClick={() => handleMetricCardClick("unallocated-receipts")}
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-purple-100">
                    <Receipt className="h-4 w-4 text-purple-600" />
                  </div>
                  {selectedMetricFilter === "unallocated-receipts" && (
                    <Badge variant="secondary" className="text-xs">Active</Badge>
                  )}
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Unallocated Receipts
                </p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(summaryMetrics.unallocatedReceiptsAmount, "USD")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {summaryMetrics.unallocatedReceiptsCount} receipt{summaryMetrics.unallocatedReceiptsCount !== 1 ? 's' : ''} with outstanding balance
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card>
        <CardHeader>
          {/* Header with Title and Settings */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold">All Transactions</h2>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => handleExport("excel")}
                title="Export to Excel"
              >
                <FileSpreadsheet className="h-4 w-4" />
              </Button>
              <ColumnConfigDialog
                columnVisibility={columnVisibility}
                columnOrder={columnOrder}
                onApply={handleColumnConfigApply}
              />
            </div>
          </div>

          {/* Search Bar and Filters Button */}
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by reference, customer, or description..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilterSheetOpen(true)}
              className="gap-2 whitespace-nowrap"
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Clear Filters
              </Button>
            )}
            {/* Default Filter Indicator */}
            {hasDefaultFilter && !hasActiveFilters && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 px-3 py-1.5 rounded-md border border-blue-200">
                <Filter className="h-3.5 w-3.5 text-blue-600" />
                <span className="text-blue-700">
                  Default filter active: <span className="font-medium">Outstanding Balance &gt; 0</span>
                </span>
                <button
                  onClick={() => {
                    setFilters({
                      ...filters,
                      outstandingBalanceMin: undefined,
                    });
                    toast.info("Default filter removed");
                  }}
                  className="ml-1 text-blue-600 hover:text-blue-800 transition-colors"
                  title="Remove default filter"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        paginatedTransactions.length > 0 &&
                        paginatedTransactions.every((t) => selectedRows.has(t.id))
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  {columnVisibility.referenceNumber && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("referenceNumber")}
                    >
                      <div className="flex items-center gap-1">
                        Reference Number
                        {sortColumn === "referenceNumber" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.transactionDate && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("transactionDate")}
                    >
                      <div className="flex items-center gap-1">
                        Transaction Date
                        {sortColumn === "transactionDate" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.dueDate && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("dueDate")}
                    >
                      <div className="flex items-center gap-1">
                        Due Date
                        {sortColumn === "dueDate" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.customerName && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("customerName")}
                    >
                      <div className="flex items-center gap-1">
                        Customer Name
                        {sortColumn === "customerName" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.transactionType && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("transactionType")}
                    >
                      <div className="flex items-center gap-1">
                        Type
                        {sortColumn === "transactionType" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.amount && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("amount")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Gross
                        {sortColumn === "amount" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.outstandingAmount && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("outstandingAmount")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Outstanding
                        {sortColumn === "outstandingAmount" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.status && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortColumn === "status" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.description && <TableHead>Description</TableHead>}
                  {columnVisibility.createdBy && <TableHead>Created By</TableHead>}
                  {columnVisibility.modifiedBy && <TableHead>Modified By</TableHead>}
                  {/* New optional columns */}
                  {columnVisibility.accountCode && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("accountCode")}
                    >
                      <div className="flex items-center gap-1">
                        Account
                        {sortColumn === "accountCode" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.netAmount && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("netAmount")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Net
                        {sortColumn === "netAmount" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.vatAmount && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("vatAmount")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Tax
                        {sortColumn === "vatAmount" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.customerReference && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("customerReference")}
                    >
                      <div className="flex items-center gap-1">
                        Customer Reference
                        {sortColumn === "customerReference" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.periodNumber && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("periodNumber")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Period Number
                        {sortColumn === "periodNumber" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.year && (
                    <TableHead
                      className="cursor-pointer text-right"
                      onClick={() => handleSort("year")}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Year
                        {sortColumn === "year" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.onDispute && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("onDispute")}
                    >
                      <div className="flex items-center gap-1">
                        On Dispute
                        {sortColumn === "onDispute" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.subLedger && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("subLedger")}
                    >
                      <div className="flex items-center gap-1">
                        Sub Ledger
                        {sortColumn === "subLedger" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.orderNumber && (
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("orderNumber")}
                    >
                      <div className="flex items-center gap-1">
                        Order Number
                        {sortColumn === "orderNumber" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                  )}
                  {columnVisibility.actions && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={Object.values(columnVisibility).filter(Boolean).length + 1}
                      className="h-24 text-center"
                    >
                      No transactions found. Try adjusting your filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedRows.has(transaction.id)}
                          onCheckedChange={(checked) =>
                            handleSelectRow(transaction.id, checked as boolean)
                          }
                        />
                      </TableCell>
                      {columnVisibility.referenceNumber && (
                        <TableCell>
                          <button
                            onClick={() => handleViewTransaction(transaction)}
                            className="text-primary hover:underline font-medium"
                          >
                            {transaction.referenceNumber}
                          </button>
                        </TableCell>
                      )}
                      {columnVisibility.transactionDate && (
                        <TableCell>{formatDateDDMMYYYY(transaction.transactionDate)}</TableCell>
                      )}
                      {columnVisibility.dueDate && (
                        <TableCell>
                          {transaction.dueDate ? formatDateDDMMYYYY(transaction.dueDate) : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.customerName && (
                        <TableCell>
                          <button
                            onClick={() => navigate(`/customers/${transaction.customerId}`)}
                            className="text-primary hover:underline font-medium transition-colors"
                            title={`View details for ${transaction.customerName}`}
                          >
                            {transaction.customerName}
                          </button>
                        </TableCell>
                      )}
                      {columnVisibility.transactionType && (
                        <TableCell>
                          <TransactionTypeBadge type={transaction.transactionType} />
                        </TableCell>
                      )}
                      {columnVisibility.amount && (
                        <TableCell className="text-right font-bold">
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </TableCell>
                      )}
                      {columnVisibility.outstandingAmount && (
                        <TableCell className="text-right font-medium">
                          {transaction.outstandingAmount !== undefined
                            ? formatCurrency(transaction.outstandingAmount, transaction.currency)
                            : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.status && (
                        <TableCell>
                          <TransactionStatusBadge status={transaction.status} />
                        </TableCell>
                      )}
                      {columnVisibility.description && (
                        <TableCell className="max-w-[300px] truncate">
                          {transaction.description}
                        </TableCell>
                      )}
                      {columnVisibility.createdBy && (
                        <TableCell>{transaction.createdBy}</TableCell>
                      )}
                      {columnVisibility.modifiedBy && (
                        <TableCell>{transaction.modifiedBy || "-"}</TableCell>
                      )}
                      {/* New optional columns */}
                      {columnVisibility.accountCode && (
                        <TableCell>{transaction.accountCode || "-"}</TableCell>
                      )}
                      {columnVisibility.netAmount && (
                        <TableCell className="text-right">
                          {transaction.netAmount !== undefined
                            ? formatCurrency(transaction.netAmount, transaction.currency)
                            : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.vatAmount && (
                        <TableCell className="text-right">
                          {transaction.vatAmount !== undefined
                            ? formatCurrency(transaction.vatAmount, transaction.currency)
                            : "-"}
                        </TableCell>
                      )}
                      {columnVisibility.customerReference && (
                        <TableCell>{transaction.customerReference || "-"}</TableCell>
                      )}
                      {columnVisibility.periodNumber && (
                        <TableCell className="text-right">{transaction.periodNumber || "-"}</TableCell>
                      )}
                      {columnVisibility.year && (
                        <TableCell className="text-right">{transaction.year || "-"}</TableCell>
                      )}
                      {columnVisibility.onDispute && (
                        <TableCell>
                          {transaction.onDispute ? (
                            <Badge variant="destructive" className="text-xs">Yes</Badge>
                          ) : (
                            <span className="text-muted-foreground">No</span>
                          )}
                        </TableCell>
                      )}
                      {columnVisibility.subLedger && (
                        <TableCell>{transaction.subLedger || "-"}</TableCell>
                      )}
                      {columnVisibility.orderNumber && (
                        <TableCell>{transaction.orderNumber || "-"}</TableCell>
                      )}
                      {columnVisibility.actions && (
                        <TableCell>
                          {/* Actions menu removed as per requirements */}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {paginatedTransactions.length > 0 && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1} to{" "}
                {Math.min(currentPage * pageSize, sortedTransactions.length)} of{" "}
                {sortedTransactions.length} results
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rows per page:</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 gap-1">
                        {pageSize}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {[10, 25, 50, 100].map((size) => (
                        <DropdownMenuItem
                          key={size}
                          onClick={() => {
                            setPageSize(size);
                            setCurrentPage(1);
                          }}
                        >
                          {size}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filter Sheet */}
      <FilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={filters}
        onFiltersChange={setFilters}
        savedPresets={savedPresets}
        activePresetId={activePresetId}
        onApplyPreset={handleApplyPreset}
        onDeletePreset={handleDeletePreset}
        onSavePreset={() => setSavePresetDialogOpen(true)}
        onClearAll={handleClearFilters}
      />

      {/* Detail Sheet */}
      <TransactionDetailSheet
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        transaction={selectedTransaction}
        onViewDocument={handleViewDocument}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete transaction {transactionToDelete?.referenceNumber}. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Preset Dialog */}
      <Dialog open={savePresetDialogOpen} onOpenChange={setSavePresetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Filter Preset</DialogTitle>
            <DialogDescription>
              Save your current filters as a preset for quick access later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="preset-name">Preset Name</Label>
              <Input
                id="preset-name"
                placeholder="e.g., High Value Invoices"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePreset}>
              <Save className="mr-2 h-4 w-4" />
              Save Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Reminders Dialog */}
      <SendRemindersDialog
        open={sendRemindersDialogOpen}
        onOpenChange={setSendRemindersDialogOpen}
        transactions={selectedTransactions}
        onSend={handleSendReminders}
      />

      {/* Allocate Receipt Dialog */}
      <AllocateReceiptDialog
        open={allocateReceiptDialogOpen}
        onOpenChange={setAllocateReceiptDialogOpen}
        receipts={selectedTransactions}
        availableInvoices={transactions}
        onAllocate={handleAllocateReceipt}
      />



      {/* Selection Toolbar - Floating at bottom when transactions are selected */}
      {selectedRows.size > 0 && (
        <SelectionToolbar
          selectedCount={selectedRows.size}
          totals={selectionTotals}
          selectedTransactions={selectedTransactions}
          onClearSelection={() => setSelectedRows(new Set())}
          onExportSelected={handleExportSelected}
          onSendReminders={() => setSendRemindersDialogOpen(true)}
          onAllocateReceipt={() => setAllocateReceiptDialogOpen(true)}
          onSendCopyInvoice={() => {
            toast.success(`Sending copy invoice for ${selectedRows.size} invoice(s)`);
          }}
        />
      )}
    </div>
  );
}
